/**
 * web/spaceHud.ts
 * Pure-DOM HUD overlay for World 2 (the space shmup).
 *
 * Reads `engine/spaceHudSnapshot` on a 100 ms throttle. Mirrors
 * {@link mountHud} but only renders hearts + world / level label.
 *
 * @module web/spaceHud
 */

/// <reference lib="dom" />

import { getSpaceHudSnapshot } from '@src/game/engine/spaceHudSnapshot';

const HUD_UPDATE_INTERVAL_MS = 100;
const MAX_HEARTS_DISPLAY = 6;

/** Build hearts string capped at {@link MAX_HEARTS_DISPLAY} glyphs. */
export function spaceHeartsString(health: number, maxHp: number): string {
  const displayMax = Math.min(maxHp, MAX_HEARTS_DISPLAY);
  const filled = Math.max(0, Math.min(health, displayMax));
  const empty = displayMax - filled;
  return '♥'.repeat(filled) + '♡'.repeat(empty);
}

/** Handle for a mounted space HUD. */
export interface SpaceHudHandle {
  destroy: () => void;
  setActive: (active: boolean) => void;
}

/** Mount the space HUD strip inside `host` and start a RAF poll. */
export function mountSpaceHud(host: HTMLElement): SpaceHudHandle {
  const strip = document.createElement('div');
  strip.className = 'sf-hud-strip sf-hud-space';
  strip.setAttribute('aria-hidden', 'true');

  const hearts = document.createElement('span');
  hearts.className = 'sf-hud-hearts';
  const world = document.createElement('span');
  world.className = 'sf-hud-text sf-hud-world';
  world.textContent = 'WORLD 2';
  const level = document.createElement('span');
  level.className = 'sf-hud-text sf-hud-level';
  strip.append(hearts, world, level);
  host.append(strip);

  let lastHearts = '';
  let lastLevel = '';
  let lastUpdate = 0;
  let active = true;
  let rafId = 0;

  const draw = (): void => {
    const s = getSpaceHudSnapshot();
    const next = spaceHeartsString(s.health, s.maxHealth);
    if (next !== lastHearts) {
      hearts.textContent = next;
      lastHearts = next;
    }
    if (s.levelName !== lastLevel) {
      level.textContent = s.levelName;
      lastLevel = s.levelName;
    }
  };

  const tick = (ts: number): void => {
    if (!active) { rafId = 0; return; }
    if (ts - lastUpdate >= HUD_UPDATE_INTERVAL_MS) {
      lastUpdate = ts;
      draw();
    }
    rafId = requestAnimationFrame(tick);
  };

  draw();
  rafId = requestAnimationFrame(tick);

  return {
    destroy: () => {
      active = false;
      if (rafId) cancelAnimationFrame(rafId);
      strip.remove();
    },
    setActive: (next: boolean) => {
      if (next && !active) {
        active = true;
        lastUpdate = 0;
        rafId = requestAnimationFrame(tick);
      } else {
        active = next;
      }
    },
  };
}
